// Phase 81vv — Header cho PickLeague homepage VĐV
// Phase 83i — Avatar from collection (DiceBear avataaars) for cross-app sync.
// Phase 83y.38 — Replace SealCheck "Tick: Xanh" với text badge "Đã xác thực"
// (xanh) / "Chờ duyệt điểm trình" (cam) theo trạng thái rating_verified.
// Phase 84.M.23 — Thay nút chuông placeholder bằng AthleteNotificationButton
// shared (real-data + drawer thay vì toast placeholder).
// Phase 84.M.38 — Hỗ trợ guest mode: header hiển thị 2 nút "Đăng nhập" /
// "Đăng ký" thay cho tên + tick + bell khi chưa login.
// Phase 84.M.40 — Dùng <Logo variant="wordmark"> từ branding (admin) thay
// cho ký tự "P" hardcoded ở cả guest mode + logged-in mode.
import { m as motion } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import { SignIn, UserPlus } from "@phosphor-icons/react";
import { resolveCollectionAvatar } from "../../lib/avatarPool";
import AthleteNotificationButton from "../athlete/shared/AthleteNotificationButton";
import UserMessagesIconButton from "../messages/UserMessagesIconButton";
import RoleSwitchButtons from "../common/RoleSwitchButtons";
import Logo from "../common/Logo";

export default function Header({ user }) {
  const navigate = useNavigate();
  const isGuest = !user || !user.id;
  const greeting = user?.full_name || user?.username || "Khách";
  const avatarUrl = resolveCollectionAvatar(user || greeting);
  const verified = !!user?.rating_verified;
  // Task 2 — Nút chuyển role cạnh tên VĐV. Mobile: nếu chỉ 1 role khác → label
  // ngắn ("Trọng tài"/"Quản trị"), nhiều role → icon-only để tiết kiệm chỗ.
  const otherRoleCount = (Array.isArray(user?.roles) ? user.roles : []).filter(
    (r) => r !== "athlete" && ["referee", "organizer", "admin", "super_admin"].includes(r)
  ).length;

  if (isGuest) {
    return (
      <header
        className="pl-safe-pt sticky top-0 z-40 px-5 pt-6 pb-3 bg-[#060B14]/95 backdrop-blur-xl"
        data-testid="home-header"
      >
        <div className="flex items-center justify-between gap-3">
          <Link to="/" className="shrink-0" style={{ filter: "drop-shadow(0 0 14px rgba(255,138,0,0.3))" }}>
            <Logo variant="wordmark" size={36} priority alt="PickLeague" testid="home-header-logo" />
          </Link>
          <div className="flex items-center gap-1.5 shrink-0">
            <Link
              to="/login"
              className="inline-flex items-center gap-1 px-3 h-9 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white text-[11.5px] font-black tracking-wider uppercase shadow-[0_4px_14px_rgba(255,138,0,0.4)] active:scale-95 transition"
              data-testid="home-header-login-btn"
            >
              <SignIn size={12} weight="bold" />
              Đăng nhập
            </Link>
            {/* Phase 85.A.156 — Bỏ `hidden xs:inline-flex sm:inline-flex` để
                nút Đăng ký hiện CẢ TRÊN MOBILE. Trước ẩn dưới 640px khiến
                user mới không biết tìm nút Đăng ký ở đâu. */}
            <Link
              to="/register"
              className="inline-flex items-center gap-1 px-3 h-9 rounded-xl bg-[#A8FF00]/[0.12] border border-[#A8FF00]/55 text-[#A8FF00] text-[11.5px] font-black tracking-wider uppercase hover:bg-[#A8FF00]/20 active:scale-95 transition shadow-[0_2px_10px_rgba(168,255,0,0.18)]"
              data-testid="home-header-register-btn"
            >
              <UserPlus size={12} weight="bold" />
              Đăng ký
            </Link>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header
      className="pl-safe-pt sticky top-0 z-40 px-5 pt-6 pb-3 bg-[#060B14]/95 backdrop-blur-xl"
      data-testid="home-header"
    >
      {/* Phase 84.M.40 — Brand row trên cùng cho user đã đăng nhập, hiển thị
          wordmark PickLeague + chuông notification ở góc phải. */}
      <div className="flex items-center justify-between mb-2.5">
        <Link to="/" className="shrink-0" style={{ filter: "drop-shadow(0 0 12px rgba(255,138,0,0.28))" }}>
          <Logo variant="wordmark" size={30} priority alt="PickLeague" testid="home-header-logo" />
        </Link>
        <div className="flex items-center gap-1">
          <UserMessagesIconButton />
          <AthleteNotificationButton testId="home-header-bell" />
        </div>
      </div>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <motion.div
            whileTap={{ scale: 0.95 }}
            className="relative shrink-0"
          >
            <div
              className="absolute inset-0 rounded-full"
              style={{
                boxShadow:
                  "0 0 24px rgba(255,138,0,0.45), 0 0 12px rgba(255,138,0,0.6)",
              }}
            />
            <img
              src={avatarUrl}
              alt={greeting}
              className="relative w-14 h-14 rounded-full border-2 border-[#FF8A00]/70 object-cover bg-[#0B1220]"
              data-testid="home-header-avatar"
            />
          </motion.div>
          <div className="min-w-0">
            <div className="text-[12px] text-[#94A3B8] font-medium tracking-wide">
              Xin chào,
            </div>
            <div className="flex items-center gap-1.5">
              <div
                className="text-[18px] font-bold text-white truncate max-w-[170px]"
                data-testid="home-header-name"
              >
                {greeting}
              </div>
              <span className="text-[16px]">👑</span>
            </div>
            <div className="flex items-center gap-1 mt-0.5">
              {verified ? (
                <span
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 text-[9.5px] font-black tracking-wider uppercase"
                  data-testid="home-header-verified"
                >
                  Đã xác thực
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => navigate("/athlete/profile")}
                  className="inline-flex items-center px-1.5 py-0.5 rounded-full bg-[#FF8A00]/15 border border-[#FF8A00]/40 text-[#FF8A00] text-[9.5px] font-black tracking-wider uppercase home-header-unverified-pulse cursor-pointer hover:bg-[#FF8A00]/25 active:scale-95 transition"
                  data-testid="home-header-unverified"
                  title="Bấm để mở mục Chờ duyệt điểm trình"
                  aria-label="Mở mục Chờ duyệt điểm trình"
                >
                  Chờ duyệt điểm trình
                </button>
              )}
            </div>
          </div>
        </div>
        {/* Task 2 — Role switch cạnh tên VĐV (chỉ hiện khi có role khác) */}
        {otherRoleCount > 0 && (
          <div className="shrink-0 ml-2" data-testid="home-header-role-switch">
            <div className="sm:hidden">
              <RoleSwitchButtons currentRole="athlete" user={user} compact={otherRoleCount > 1} short />
            </div>
            <div className="hidden sm:block">
              <RoleSwitchButtons currentRole="athlete" user={user} short />
            </div>
          </div>
        )}
        {/* Phase 84.M.40 — Bell đã chuyển lên brand row phía trên, tránh trùng lặp. */}
      </div>
    </header>
  );
}
